
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import {
  fetchResources,
  createResource as createResourceQuery,
  updateResource as updateResourceQuery,
  deleteResource as deleteResourceQuery,
  type Resource
} from '@/lib/queries/resources';

// Re-export Resource type for external usage
export type { Resource } from '@/lib/queries/resources';

export const useResources = (tenantId: string | null) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['resources', tenantId],
    queryFn: async () => {
      if (!tenantId || !user) return [];
      return fetchResources(tenantId);
    },
    enabled: !!tenantId && !!user,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });

  const resources = data || [];

  const createResource = async (resource: Omit<Resource, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>) => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    try {
      const data = await createResourceQuery(tenantId, resource);

      // Update cache optimistically
      queryClient.setQueryData(['resources', tenantId], (old: Resource[] | undefined) => {
        return [data, ...(old || [])];
      });

      return { data, error: null as any };
    } catch (error) {
      console.error('Error creating resource:', error);
      return { data: null, error };
    }
  };

  const updateResource = async (id: string, updates: Partial<Omit<Resource, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>>) => {
    try {
      const data = await updateResourceQuery(id, updates);

      // Update cache optimistically
      queryClient.setQueryData(['resources', tenantId], (old: Resource[] | undefined) => {
        return (old || []).map(r => r.id === id ? data : r);
      });

      return { data, error: null as any };
    } catch (error) {
      console.error('Error updating resource:', error);
      return { data: null, error };
    }
  };

  const deleteResource = async (id: string) => {
    try {
      await deleteResourceQuery(id);

      // Update cache optimistically
      queryClient.setQueryData(['resources', tenantId], (old: Resource[] | undefined) => {
        return (old || []).filter(r => r.id !== id);
      });

      return { error: null as any };
    } catch (error) {
      console.error('Error deleting resource:', error);
      return { error };
    }
  };

  return {
    resources,
    isLoading,
    createResource,
    updateResource,
    deleteResource,
    refetch,
  };
};
